import React from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { motion } from "framer-motion";

/**
 * ErrorBoundary - Catches React errors and prevents white-screen crashes
 * 
 * Features:
 * - Graceful error UI instead of white screen
 * - Error details visible in development mode
 * - "Try Again" button to reset error state
 * - Works with all React components
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error for debugging
    console.error("❌ Error caught by ErrorBoundary:", error);
    console.error("Component Stack:", errorInfo.componentStack);

    this.setState((prev) => ({
      error,
      errorInfo,
      errorCount: prev.errorCount + 1,
    }));

    // Optional: Send to error tracking service (Sentry, etc.)
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === "development";
      const { error, errorInfo, errorCount } = this.state;

      return (
        <div className="min-h-screen bg-midnight text-zinc-50 flex items-center justify-center px-4"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          <div className="max-w-sm w-full">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-zinc-900/70 shadow-sm ring-1 ring-white/10 p-6"
            >
              {/* Error Icon & Message */}
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-2xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <div className="font-semibold">Something went wrong</div>
                  <div className="text-sm text-zinc-400">
                    We're sorry, an unexpected error occurred.
                  </div>
                </div>
              </div>

              {/* Error Details (Development Only) */}
              {isDevelopment && error && (
                <div className="mb-4 space-y-2">
                  <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                    <div className="text-xs font-semibold text-red-300 mb-1">
                      Error Message:
                    </div>
                    <div className="text-xs font-mono text-red-300 break-words">
                      {error.toString()}
                    </div>
                  </div>

                  {errorInfo && (
                    <details className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                      <summary className="cursor-pointer font-semibold text-xs text-zinc-300 hover:text-zinc-200 transition">
                        📋 Stack Trace ({errorCount} error{errorCount > 1 ? 's' : ''})
                      </summary>
                      <pre className="mt-2 overflow-auto whitespace-pre-wrap text-xs text-zinc-400 max-h-40">
                        {errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Production-Friendly Help */}
              {!isDevelopment && (
                <div className="mb-4 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <div className="text-xs text-zinc-300">
                    Please try reloading the app. If the problem persists, contact support.
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={this.handleReset}
                  className="flex-1 rounded-xl bg-white text-zinc-900 py-3 font-semibold hover:bg-zinc-100 transition flex items-center justify-center gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Try Again
                </button>

                {/* Reload Button */}
                <button
                  onClick={() => window.location.reload()}
                  className="flex-1 rounded-xl bg-zinc-800 text-white py-3 font-semibold hover:bg-zinc-700 transition"
                  title="Hard refresh the app"
                >
                  🔄 Reload
                </button>
              </div>

              {/* Development Mode Indicator */}
              {isDevelopment && (
                <div className="mt-4 p-2 rounded bg-blue-500/10 border border-blue-500/20">
                  <div className="text-xs text-blue-300">
                    🧪 Development Mode - Stack trace visible above
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
